import { useAppSelector } from "@/store/store-hooks";
import {
  convertToDateString,
  FaroPopover,
  FaroText,
  LocationMarkerPopoverImg,
  grey,
  NO_TRANSLATE_CLASS,
  selectIElementWorldPosition,
} from "@faro-lotv/app-component-toolbox";
import { IElementRoomSection } from "@faro-lotv/ielement-types";
import { Box, CardMedia, Stack } from "@mui/material";
import { useState, type JSX } from "react";
import { AppAwareHtml } from "../renderers/app-aware-html";
import { POPPER_MODIFIERS } from "./placeholder-preview";

type LocationMarkerPopoverProps = {
  /** The location marker which is currently being hovered */
  locationMarker: IElementRoomSection;
};

/**
 * @returns  A popover to remind user the location marker needs to be captured
 */
export function LocationMarkerPopover({
  locationMarker,
}: LocationMarkerPopoverProps): JSX.Element | null {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const position = useAppSelector(
    selectIElementWorldPosition(locationMarker.id),
  );

  return (
    <AppAwareHtml
      position={position}
      style={{
        backdropFilter: "blur(4px) brightness(40%)",
        borderRadius: 6,
      }}
    >
      {/* anchor element where the popper attaches to */}
      <Box component="div" ref={setAnchorEl} />
      {anchorEl && (
        <FaroPopover
          open
          dark
          disablePortal
          placement="bottom"
          anchorEl={anchorEl}
          showCloseButton={false}
          sx={{
            // Remove default padding around content and make it fit the content
            " .MuiPaper-root": {
              // Remove padding on top, between content and arrow
              "> .MuiStack-root": {
                gap: 0,
              },
              p: 0,
              maxWidth: "fit-content",
            },
          }}
          modifiers={POPPER_MODIFIERS}
        >
          <Box
            component="div"
            sx={{
              minWidth: 310,
              background: grey[950],
              padding: "12px",
              borderRadius: "4px",
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                mb: "10px",
              }}
            >
              <FaroText
                className={NO_TRANSLATE_CLASS}
                variant="labelM"
                color={grey[100]}
              >
                {locationMarker.name}
              </FaroText>
              <FaroText
                className={NO_TRANSLATE_CLASS}
                variant="bodyXS"
                color={grey[100]}
              >
                {locationMarker.createdAt
                  ? convertToDateString(locationMarker.createdAt)
                  : undefined}
              </FaroText>
            </Stack>
            <CardMedia
              component="img"
              height={48}
              width="100%"
              image={LocationMarkerPopoverImg}
              sx={{
                objectFit: "fill",
              }}
            />
            <Stack direction="column" sx={{ alignItems: "center" }}>
              <FaroText
                variant="heading16"
                color={grey[100]}
                sx={{ mt: 1, mb: 1 }}
              >
                Photo needed here!
              </FaroText>
              <FaroText
                variant="bodyM"
                color={grey[300]}
                sx={{ mb: 0.2, textAlign: "center" }}
              >
                This location requires a new photo capture. Open the JobWalk app
                to capture and upload the latest image.
              </FaroText>
            </Stack>
          </Box>
        </FaroPopover>
      )}
    </AppAwareHtml>
  );
}
